import React, { useContext, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { useIntl } from 'react-intl';
import { audioMessages } from './i18n';
import { configurationContext } from '../../../contexts/configuration';
import { submitContext } from '../../../contexts/submit.context';
import { IChatVoiceMessage, IFileResource } from '@enuan/chat__utils';
import { Modal } from '../../UI/Modal/Modal';
import { LongPressEventType, useLongPress } from 'use-long-press';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { isVoiceMessage } from '@enuan/chat__utils';
import { configContext } from '../../../contexts/config/context';
import { DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { ReactComponent as RecordIcon } from '@/assets/record_audio.svg';
import { ReactComponent as TranscriptIcon } from '@/assets/transcript.svg';
import { Pause as PauseIcon } from '@mui/icons-material';

type IRecordAudioProps = {
	disabled?: boolean;
	stopRecording: (
		fileName?: string,
	) => Promise<IChatVoiceMessage<IFileResource>>;
	startRecording: () => Promise<void>;
};

export const RecordAudioButton = styled(
	({
		disabled,
		startRecording,
		stopRecording,
		className,
	}: IRecordAudioProps & { className?: string }) => {
		const logError = useLogError('RecordAudio');

		const intl = useIntl();
		const permission_error = intl.formatMessage(audioMessages.permission_error);
		const { transcript_input, speech_input } = useContext(configurationContext);
		const { message, action, setMessage, setAction, setError, submitForm } =
			useContext(submitContext);

		const isRecording = action === 'recording' && !message;

		const isRecorded = isVoiceMessage(message);

		const [isModalOpen, setModalOpen] = useState<boolean>(false);
		useDidUpdateEffect(() => {
			if (isModalOpen) {
				setAction('recording');
			}
			if (!isRecorded && !isModalOpen) {
				setAction(undefined);
			}
		}, [isModalOpen, setAction, message]);

		const isModalEnabled = transcript_input && speech_input;

		const { isRecordAudioApproved, isRecordAudioEnabled } =
			useContext(configContext);

		const onClick = useCallback(() => {
			// console.log('test', 'onClick');

			if (isRecording) {
				setAction(undefined);

				return stopRecording()
					.then(setMessage)
					.catch((error) => {
						logError('onClickRecording:submit', { error });
					});
			}
			if (isModalEnabled) return setModalOpen(true);

			if (speech_input) {
				startRecording();
			} else if (transcript_input) {
				// TODO implements transcript text
				logError('transcriptInput', {
					error: new Error('Missing implementation'),
				});
				setError(permission_error);
			}
		}, [
			isRecording,
			isModalEnabled,
			speech_input,
			transcript_input,
			setAction,
			stopRecording,
			setMessage,
			logError,
			startRecording,
			setError,
			permission_error,
		]);

		const onFinish = useCallback(() => {
			if (!disabled && isRecordAudioApproved && isRecording) {
				setAction(undefined);
				stopRecording()
					.then(submitForm)
					.catch((error) => logError('onPressRecording:submit', { error }));
			}
		}, [
			disabled,
			isRecordAudioApproved,
			isRecording,
			logError,
			setAction,
			stopRecording,
			submitForm,
		]);

		const bind = useLongPress(
			() => {
				// console.log('test', 'onPressRecording');
				if (
					!disabled &&
					// && isRecordAudioApproved
					!isRecording
				) {
					startRecording();
				}
			},
			{
				detect: LongPressEventType.Touch,
				onFinish,
			},
		);

		if (!isRecordAudioEnabled) return null;

		return (
			<div className={className}>
				{(isRecording || isRecorded) && (
					<button
						type="button"
						unselectable="on"
						className={classNames(
							'form__action',
							'form__record-audio',
							'form__record-audio--abort',
						)}
						onClick={() => {
							setAction(undefined);
							setMessage(undefined);
							stopRecording();
						}}
					>
						<DeleteForeverIcon className="icon" />
					</button>
				)}
				<button
					type="button"
					unselectable="on"
					className={classNames(
						'form__action',
						'form__record-audio',
						disabled && 'form__action--disabled',
						isModalOpen && 'form__record-audio--open',
						isRecording && 'form__record-audio--recording',
						!isRecordAudioApproved && 'form__action--disabled',
					)}
					disabled={disabled || isRecordAudioApproved === false}
					{...bind}
					onClick={onClick}
					onContextMenu={(e) => {
						e.preventDefault();
						e.stopPropagation();
						return false;
					}}
				>
					{isRecording ? (
						<PauseIcon className="icon pause" />
					) : (
						<RecordIcon className="icon record" />
					)}
				</button>
				{isModalEnabled && (
					<RecordAudioModal
						open={isModalOpen}
						onClose={() => setModalOpen(false)}
						onRecordAudio={() => {
							setModalOpen(false);
							startRecording().catch((error) => {
								logError('onClickRecording:submit', { error });
							});
						}}
						onTranscript={() => {
							setModalOpen(false);
							logError('transcriptInput', {
								error: new Error('Missing implementation'),
							});
							setError(permission_error);
						}}
					/>
				)}
			</div>
		);
	},
	{ label: 'RecordAudio', target: 'RecordAudio-root' },
)(({ theme }) => {
	const bordeaux = theme.colors.bordeaux;
	const pulse = keyframes({
		'0%': {
			transform: 'scale(0.95)',
			filter: `drop-shadow(0 0 0 ${theme.mixins.hexWithOpacity(
				bordeaux,
				0.7,
			)})`,
		},
		'70%': {
			transform: 'scale(1)',
			filter: `drop-shadow(0 0 0.7em ${theme.mixins.hexWithOpacity(
				bordeaux,
				0.7,
			)})`,
		},
		'100%': {
			transform: 'scale(0.95)',
			filter: `drop-shadow(0 0 0 ${theme.mixins.hexWithOpacity(bordeaux, 0)})`,
		},
	});

	return {
		display: 'contents',

		'& .form__record-audio': {
			'&.form__record-audio--open': {
				'& .st0': {
					fill: 'var(--chat__footer__form__record-audio--hover_color)',
				},
				transform: 'scale(1.25)',
			},
			'& .icon': {
				stroke: 'none',
				'& .st0': {
					stroke: 'none',
				},
			},
			'&.form__record-audio--abort, &.form__record-audio--abort:hover': {
				marginLeft: '0.5em',
				transform: 'scale(1.25)',
				'& .icon': {
					transform: 'scale(1.25)',
					fill: 'var(--chat__footer__form__recording-audio_color)',
				},
			},
			'&.form__record-audio--recording, &.form__record-audio--recording:hover':
				{
					transform: 'scale(1.25)',
					'& .icon': {
						'&.record .st0': {
							fill: 'var(--chat__footer__form__recording-audio_color)',
						},
						'&.pause': {
							fill: 'var(--chat__footer__form__recording-audio_color)',
						},
						filter: `drop-shadow(0 0 0 ${theme.mixins.hexWithOpacity(
							bordeaux,
							1,
						)})`,
						transform: 'scale(1)',
						animation: `${pulse} 1s infinite`,
					},
				},
			'&:not(:disabled):not(.form__action--disabled)': {
				'&:hover': {
					'& .st0': {
						fill: 'var(--chat__footer__form__record-audio--hover_color)',
					},
				},
			},
		},
	};
});

type RecordAudioModalProps = {
	open: boolean;
	onClose: () => void;
	onRecordAudio: () => void;
	onTranscript: () => void;
	className?: string;
};

const RecordAudioModal = styled(
	({
		open,
		onClose,
		onRecordAudio,
		onTranscript,
		className,
	}: RecordAudioModalProps) => {
		const intl = useIntl();
		const title = intl.formatMessage(audioMessages.title);
		const audioText = intl.formatMessage(audioMessages.audio);
		const transcriptText = intl.formatMessage(audioMessages.transcript);

		return (
			<Modal
				open={open}
				onClose={onClose}
				title={title}
				className={classNames(className, 'record-audio-modal')}
			>
				<div className="modal__list">
					<button
						type="button"
						onClick={onRecordAudio}
						className={classNames('list__item', 'audio')}
					>
						<RecordIcon className="icon record-audio" />
						<span>{audioText}</span>
					</button>
					<button
						type="button"
						onClick={onTranscript}
						className={classNames('list__item', 'transcript')}
					>
						<TranscriptIcon className="icon transcript" />
						<span>{transcriptText}</span>
					</button>
				</div>
			</Modal>
		);
	},
	{
		target: 'RecordAudioModal-root',
		label: 'RecordAudioModal',
	},
)({
	'& .st1': {
		fill: 'none',
		stroke: 'var(--icon_color)',
		strokeWidth: 0.7182,
		strokeLinecap: 'round',
		strokeLinejoin: 'round',
		strokeMiterlimit: 10,
	},
});
